import {isNil, isNotNil} from 'ramda';

import {NumericBoundBody, RelativeFilterChoice, RelativeFilterConfigBody} from '@dms/api/core';
import i18n from '@dms/i18n';

const PCT_UNIT = '%';

export type PresetSummaryTag = {
  id: string;
  label: string;
};

function pushTag(tags: PresetSummaryTag[], id: string, label: string | null) {
  if (isNotNil(label)) {
    tags.push({id, label});
  }
}

function boundSummary(
  bound: NumericBoundBody | null,
  unit: string,
  sameLabel: string
): string | null {
  if (isNil(bound)) {
    return null;
  }

  const parts: string[] = [];

  if (isNotNil(bound.from) && bound.from !== 0) {
    parts.push(`${bound.from}${unit}`);
  }
  if (isNotNil(bound.to) && bound.to !== 0) {
    const sign = bound.to > 0 ? '+' : '';
    parts.push(`${sign}${bound.to}${unit}`);
  }

  if (parts.length === 0 && (bound.from === 0 || bound.to === 0)) {
    return sameLabel;
  }

  return parts.length > 0 ? parts.join(', ') : null;
}

function withParameterLabel(parameterLabel: string, summary: string | null): string | null {
  if (isNil(summary)) {
    return null;
  }
  return `${parameterLabel}: ${summary}`;
}

function withParameterLabelUnlessSame(
  parameterLabel: string,
  summary: string | null,
  sameLabel: string
): string | null {
  return summary === sameLabel ? summary : withParameterLabel(parameterLabel, summary);
}

function boundPairSummary(
  abs: NumericBoundBody | null,
  pct: NumericBoundBody | null,
  absUnit: string,
  parameterLabel: string,
  sameLabel: string
): string | null {
  const absSummary = isNotNil(abs) ? boundSummary(abs, absUnit, sameLabel) : null;
  const pctSummaryValue = isNotNil(pct) ? boundSummary(pct, PCT_UNIT, sameLabel) : null;
  const pctSummary = withParameterLabelUnlessSame(parameterLabel, pctSummaryValue, sameLabel);

  return isNotNil(absSummary) ? absSummary : pctSummary;
}

function propSummary(
  mode: RelativeFilterChoice | null,
  sameLabel: string,
  sameBetterLabel: string
): string | null {
  if (isNil(mode)) {
    return null;
  }
  return mode === 'same' ? sameLabel : sameBetterLabel;
}

/** Builds the list of human-readable tags shown in the preset summary. */
export function getPresetTags(config: RelativeFilterConfigBody): PresetSummaryTag[] {
  const tags: PresetSummaryTag[] = [];

  pushTag(
    tags,
    'year',
    boundSummary(
      config.year,
      i18n.t('entity.sourcing.relativeFilters.summary.unit.year'),
      i18n.t('entity.sourcing.relativeFilters.summary.registration.same')
    )
  );

  pushTag(
    tags,
    'mileage',
    boundPairSummary(
      config.mileage,
      config.mileagePct,
      i18n.t('entity.sourcing.relativeFilters.summary.unit.km'),
      i18n.t('entity.sourcing.vehiclePreview.labels.mileage'),
      i18n.t('entity.sourcing.relativeFilters.summary.mileage.same')
    )
  );

  pushTag(
    tags,
    'power',
    boundPairSummary(
      config.power,
      config.powerPct,
      i18n.t('entity.sourcing.relativeFilters.summary.unit.kw'),
      i18n.t('entity.sourcing.vehiclePreview.labels.power'),
      i18n.t('entity.sourcing.relativeFilters.summary.power.same')
    )
  );

  pushTag(
    tags,
    'price',
    boundPairSummary(
      config.price,
      config.pricePct,
      i18n.t('entity.sourcing.relativeFilters.summary.unit.eur'),
      i18n.t('entity.sourcing.relativeFilters.fields.price'),
      i18n.t('entity.sourcing.relativeFilters.summary.price.same')
    )
  );

  const featureScoreSameLabel = i18n.t('entity.sourcing.relativeFilters.summary.featureScore.same');
  pushTag(
    tags,
    'featureScore',
    withParameterLabelUnlessSame(
      i18n.t('entity.sourcing.relativeFilters.fields.featureScore'),
      boundSummary(config.featureScore, PCT_UNIT, featureScoreSameLabel),
      featureScoreSameLabel
    )
  );

  if (isNotNil(config.radius)) {
    pushTag(
      tags,
      'radius',
      i18n.t('entity.sourcing.relativeFilters.summary.radius', {
        km: config.radius,
      })
    );
  }

  pushTag(
    tags,
    'trimLevel',
    propSummary(
      config.trimLevel,
      i18n.t('entity.sourcing.relativeFilters.summary.trimLevel.same'),
      i18n.t('entity.sourcing.relativeFilters.summary.trimLevel.sameBetter')
    )
  );

  pushTag(
    tags,
    'transmission',
    propSummary(
      config.transmission,
      i18n.t('entity.sourcing.relativeFilters.summary.transmission.same'),
      i18n.t('entity.sourcing.relativeFilters.summary.transmission.sameBetter')
    )
  );

  pushTag(
    tags,
    'fuel',
    propSummary(
      config.fuel,
      i18n.t('entity.sourcing.relativeFilters.summary.fuel.same'),
      i18n.t('entity.sourcing.relativeFilters.summary.fuel.sameBetter')
    )
  );

  pushTag(
    tags,
    'body',
    propSummary(
      config.body,
      i18n.t('entity.sourcing.relativeFilters.summary.body.same'),
      i18n.t('entity.sourcing.relativeFilters.summary.body.sameBetter')
    )
  );

  pushTag(
    tags,
    'drive',
    propSummary(
      config.drive,
      i18n.t('entity.sourcing.relativeFilters.summary.drive.same'),
      i18n.t('entity.sourcing.relativeFilters.summary.drive.sameBetter')
    )
  );

  pushTag(
    tags,
    'vatDeductible',
    propSummary(
      config.vatDeductible,
      i18n.t('entity.sourcing.relativeFilters.summary.vatDeductible.same'),
      i18n.t('entity.sourcing.relativeFilters.summary.vatDeductible.sameBetter')
    )
  );

  pushTag(
    tags,
    'sellerType',
    propSummary(
      config.sellerType,
      i18n.t('entity.sourcing.relativeFilters.summary.sellerType.same'),
      i18n.t('entity.sourcing.relativeFilters.summary.sellerType.sameBetter')
    )
  );

  pushTag(
    tags,
    'seats',
    propSummary(
      config.seats,
      i18n.t('entity.sourcing.relativeFilters.summary.seats.same'),
      i18n.t('entity.sourcing.relativeFilters.summary.seats.sameBetter')
    )
  );

  return tags;
}
